// Small helpers the sidebar engine uses to read values out of API records and
// to fill `{token}` templates — the pure-data replacement for `map` callbacks.

/** Read a dot-path (e.g. "pagination.total", "status.color") from an object. */
export function getPath(obj: unknown, path: string | undefined): unknown {
  if (!path) return undefined;
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

/** String value at a dot-path, or '' when missing/non-primitive. */
export function getString(obj: unknown, path: string | undefined): string {
  const v = getPath(obj, path);
  return v == null ? '' : String(v);
}

/**
 * Fill a `{token}` template. Tokens resolve against `record` (dot-paths) first,
 * then the supplied ctx bag (URL params, userId).
 */
export function interpolate(
  template: string,
  record: unknown,
  ctx: Record<string, string | number | null | undefined> = {},
): string {
  return template.replace(/\{([^}]+)\}/g, (_m, token: string) => {
    const key = token.trim();
    const fromRecord = getPath(record, key);
    if (fromRecord != null) return String(fromRecord);
    const fromCtx = ctx[key];
    return fromCtx == null ? '' : String(fromCtx);
  });
}

/** Resolve ApiRequest params, interpolating any `{token}` string values. */
export function resolveParams(
  params: Record<string, string | number | boolean> | undefined,
  ctx: Record<string, string | number | null | undefined>,
): Record<string, string | number | boolean> | undefined {
  if (params == null) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    out[k] = typeof v === 'string' ? interpolate(v, null, ctx) : v;
  }
  return out;
}
